import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HeatmapCell, PlannedWeekData, Weekday } from '../../../types/weekOverview';
import {
  HEATMAP_CELL_SELECTOR,
  WEEKDAYS_ORDER,
  WEEKDAY_TRANSLATION_KEYS
} from '../../../constants/weekOverviewConstants';
import { buildHeatmapData } from '../../../services/heatmapService';
import type { HeatmapNavKey } from '../../../services/heatmapNavigationService';
import {
  buildHeatmapNavRows,
  getFirstCellId,
  getNavTargetCellId,
  hasCell
} from '../../../services/heatmapNavigationService';
import { HeatmapRow } from './HeatmapRow';
import { HeatmapTooltip } from './HeatmapTooltip';
import styles from './WeekHeatmap.module.css';

interface WeekHeatmapProps {
  weekData: PlannedWeekData;
  onStaffSelect?: (staffId: string) => void;
  onCellClick?: (skillId: string, staffId: string | null, day: Weekday) => void;
}

export const WeekHeatmap = ({ weekData, onStaffSelect, onCellClick }: WeekHeatmapProps) => {
  const { t } = useTranslation();
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [lastFocusedCellId, setLastFocusedCellId] = useState<string | null>(null);
  const [tooltip, setTooltip] = useState<{
    cell: HeatmapCell;
    position: { x: number; y: number };
  } | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const heatmapData = useMemo(() => buildHeatmapData(weekData), [weekData]);
  const { skills, staffBySkillId, heatmapCells, staffHeatmapCells } = heatmapData;

  const handleToggle = useCallback((skillId: string) => {
    setExpandedSkills((prev) => ({ ...prev, [skillId]: !prev[skillId] }));
  }, []);

  const handleCellHover = useCallback(
    (cell: HeatmapCell | null, position: { x: number; y: number } | null) => {
      if (cell && position) {
        setTooltip({ cell, position });
      } else {
        setTooltip(null);
      }
    },
    []
  );

  const handleCellClick = useCallback(
    (skillId: string, staffId: string | null, day: Weekday) => {
      if (onCellClick) onCellClick(skillId, staffId, day);
    },
    [onCellClick]
  );

  const handleStaffSelect = useCallback(
    (staffId: string) => {
      if (onStaffSelect) onStaffSelect(staffId);
    },
    [onStaffSelect]
  );

  const navRows = useMemo(
    () => buildHeatmapNavRows(heatmapData, expandedSkills),
    [expandedSkills, heatmapData]
  );

  // The grid is a single tab stop: one cell carries tabIndex=0 and the arrows
  // move between the rest. Deriving it also covers the collapse case — when the
  // row holding the tab stop folds away, it reverts to the first cell.
  const tabStopCellId = hasCell(navRows, lastFocusedCellId)
    ? lastFocusedCellId
    : getFirstCellId(navRows);

  const handleCellFocus = useCallback((cellId: string) => {
    setLastFocusedCellId(cellId);
  }, []);

  const handleCellNavigate = useCallback(
    (cellId: string, key: HeatmapNavKey) => {
      const targetCellId = getNavTargetCellId(navRows, cellId, key);
      if (!targetCellId || !gridRef.current) return;
      // Matching on dataset rather than an interpolated attribute selector keeps
      // skill and staff ids free of CSS escaping rules. Moving focus is enough:
      // the cell's focus handler rehomes the tab stop.
      const cells = Array.from(
        gridRef.current.querySelectorAll<HTMLButtonElement>(HEATMAP_CELL_SELECTOR)
      );
      cells.find((cell) => cell.dataset.heatmapCellId === targetCellId)?.focus();
    },
    [navRows]
  );

  const cellsBySkill = useMemo(() => {
    const map = new Map<string, HeatmapCell[]>();
    for (const c of heatmapCells) {
      const list = map.get(c.skillId) ?? [];
      list.push(c);
      map.set(c.skillId, list);
    }
    return map;
  }, [heatmapCells]);

  const tooltipSkill = tooltip ? skills.find((s) => s.id === tooltip.cell.skillId) : undefined;
  const tooltipStaff = tooltip?.cell.staffId
    ? Object.values(staffBySkillId).flat().find((s) => s.id === tooltip.cell.staffId)
    : undefined;

  return (
    <div className={styles.heatmapContainer}>
      <h2 className={styles.heatmapTitle}>{t('week-overview-heatmap-title')}</h2>
      <div ref={gridRef} className={styles.grid}>
        <div className={`${styles.headerCell} ${styles.cornerCell}`} />
        {WEEKDAYS_ORDER.map((day) => (
          <div key={day} className={styles.headerCell}>
            {t(WEEKDAY_TRANSLATION_KEYS[day])}
          </div>
        ))}
        <div className={`${styles.headerCell} ${styles.totalHeader}`}>
          {t('week-overview-heatmap-total')}
        </div>

        {skills.map((skill) => {
          const cells = cellsBySkill.get(skill.id) ?? [];
          const staffMembers = staffBySkillId[skill.id] ?? [];
          const staffCells = staffHeatmapCells.filter((c) => c.skillId === skill.id);
          return (
            <HeatmapRow
              key={skill.id}
              skill={skill}
              cells={cells}
              staffMembers={staffMembers}
              staffCells={staffCells}
              isExpanded={Boolean(expandedSkills[skill.id])}
              tabStopCellId={tabStopCellId}
              onToggle={handleToggle}
              onCellClick={handleCellClick}
              onCellHover={handleCellHover}
              onStaffSelect={handleStaffSelect}
              onCellNavigate={handleCellNavigate}
              onCellFocus={handleCellFocus}
            />
          );
        })}
      </div>

      {tooltip && (
        <HeatmapTooltip
          cell={tooltip.cell}
          position={tooltip.position}
          skill={tooltipSkill}
          staff={tooltipStaff}
        />
      )}
    </div>
  );
};
